import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { HotelWaitingList } from "admin/types/hotelWaitingList";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { Meta } from "admin/types/meta";

const key = "hotelWaitingList";
const adapter = createEntityAdapter<HotelWaitingList.WaitingList>();

const initialStateValues: HotelWaitingList.HotelWaitingListState = {
  hotelWaitingList: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchHotelWaitingLists = createAsyncThunk<
  {
    waitingLists: HotelWaitingList.WaitingList[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelWaitingLists`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      param,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/waiting_lists.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/waiting_lists.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      waitingLists: result.data.waitingLists,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelWaitingListSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelWaitingList };
      })
      .addCase(fetchHotelWaitingLists.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.waitingLists);
        state.meta = action.payload.meta;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelWaitingListByIdSelector,
  selectIds: hotelWaitingListIdsSelector,
  selectEntities: hotelWaitingListEntitiesSelector,
  selectAll: hotelWaitingListsSelector,
  selectTotal: totalHotelWaitingListsSelector,
} = adapter.getSelectors((state: RootState) => state.hotelWaitingList);

const stateSelector = (state: RootState) => state[key];

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const metaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);
